import ClearIcon from '@mui/icons-material/Clear';
import { Box, Grid, IconButton, Link } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  CompanyGdprSettingsDTO,
  getCurrentWorkspaceUser,
  getCompanyGDPRSettingsByLoggedineRecruiter,
  postGDPRSettings,
} from 'wisestep-api';
import {
  WsTitleBanner,
  WsColors,
  ControlledTextInput,
  WsTypography,
  WsTypoVariants,
  WsButton,
  WsTagsList,
  ChipsType,
  WsTypoColors,
  IWorkspace,
  IUserDTO,
} from 'wisestep-base';

import SettingsIndex from '../../components/SettingsIndex';
import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { useUserContext, useWorkspaceContext } from '../../context';
import { success, failed } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';
import { AppLayoutTheme } from '../candidates/utils/CandidateHelper';
import dataProtectionLinkGenerate from '../dataProtection/helper/dataProtectionLinkGenerate';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

interface IDataProtectionSetUp {
  name: string;
  logoName: string;
  logoUrl: string;
  email: string;
}
const defaultCompanyGdprSettingsDTO: CompanyGdprSettingsDTO = {
  companyName: 'default',
  companyNameAndLogoUrls: new Map([['default', 'default']]),
  dataPrivacyEmail: 'default',
};
const DataProtectionSetUp = () => {
  const [loggedinRecruiterDetails, setLoggedinRecruiterDetails] = useState<CompanyGdprSettingsDTO>(
    defaultCompanyGdprSettingsDTO
  );
  const [userDetails, setUserDetails] = useState<IWorkspace[]>();
  const [listCompanyInfo, setListCompanyInfo] = useState<
    { companyName: string; companyLink: string; companyId: string }[]
  >([]);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspaceInfo = useWorkspaceContext();
  const methods = useForm<IDataProtectionSetUp>();
  const { handleSubmit, getValues, reset } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  useEffect(() => {
    const getRecruiterDetails = async () => {
      const settingResponse = await getCompanyGDPRSettingsByLoggedineRecruiter();
      const response: CompanyGdprSettingsDTO = settingResponse.data;
      if (response) setLoggedinRecruiterDetails(response);
      else setLoggedinRecruiterDetails(defaultCompanyGdprSettingsDTO);
    };
    const currentUserDetails = async () => {
      try {
        const workspaceReq = await getCurrentWorkspaceUser();
        const workspaceRes: IWorkspace[] = workspaceReq.data;
        if (workspaceRes) setUserDetails(workspaceRes);
      } catch (e) {
        console.log(e);
      }
    };
    getRecruiterDetails();
    currentUserDetails();
  }, []);

  useEffect(() => {
    reset({
      name: loggedinRecruiterDetails?.companyName,
      email: loggedinRecruiterDetails?.dataPrivacyEmail,
      logoName: isEmpty(loggedinRecruiterDetails?.companyNameAndLogoUrls)
        ? ''
        : Object.keys(loggedinRecruiterDetails?.companyNameAndLogoUrls ?? {})[0],
      logoUrl: isEmpty(loggedinRecruiterDetails?.companyNameAndLogoUrls)
        ? ''
        : Object.values(loggedinRecruiterDetails?.companyNameAndLogoUrls ?? {})[0],
    });
    setListCompanyInfo(() => {
      const newState = Object.keys(loggedinRecruiterDetails?.companyNameAndLogoUrls ?? {}).map(
        (key) => {
          return {
            companyName: key,
            companyLink: loggedinRecruiterDetails?.companyNameAndLogoUrls
              ? loggedinRecruiterDetails?.companyNameAndLogoUrls[key]
              : '',
            companyId: loggedinRecruiterDetails?.id ?? '',
          };
        }
      );
      return newState;
    });
  }, [
    loggedinRecruiterDetails,
    loggedinRecruiterDetails?.companyName,
    loggedinRecruiterDetails?.companyNameAndLogoUrls,
    loggedinRecruiterDetails?.companyNameAndLogoUrls?.keys,
    loggedinRecruiterDetails?.companyNameAndLogoUrls?.values,
    loggedinRecruiterDetails?.dataPrivacyEmail,
    loggedinRecruiterDetails?.id,
    reset,
    setListCompanyInfo,
  ]);

  const onSubmit = (formData: IDataProtectionSetUp) => {
    const companyNameAndLogoUrls = {} as unknown as Map<string, string>;
    listCompanyInfo.forEach((ci) => (companyNameAndLogoUrls[ci.companyName] = ci.companyLink));
    const request: CompanyGdprSettingsDTO = {
      companyName: formData.name,
      companyNameAndLogoUrls,
      dataPrivacyEmail: formData.email,
      workspaceIdsToUseForCandidateInfoSearch: [workspaceInfo?.id ?? ''],
      recruiterId: loggedInUser?.recruiterId ?? '',
    };
    const postSetupData = async () => {
      try {
        if (request) {
          const response = await postGDPRSettings(request);
          request.id = response.data.id;
          setLoggedinRecruiterDetails(request);
          setNotification(success('New setup added successfully!'));
        }
      } catch (e) {
        console.log(e);
        setNotification(failed('Some error occured! Please try later.'));
      }
    };
    postSetupData();
  };
  const onAdd = () => {
    const name = getValues('logoName');
    const url = getValues('logoUrl');
    const id = loggedinRecruiterDetails?.id ?? '';
    setListCompanyInfo((prevState) => {
      prevState.push({
        companyName: name,
        companyLink: url,
        companyId: prevState.length ? prevState[0].companyId : id,
      });
      return [...prevState];
    });
  };
  const onDelete = (index: number) => {
    setListCompanyInfo((prevState) => {
      prevState.splice(index, 1);
      return [...prevState];
    });
  };
  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} mx={4} my={2}>
        <WsBreadCrumbs
          breadCrumbsList={getSettingBreadCrumbList(
            ISettingBreadCrumbTypes.DATA_PRIVACY,
            Pages['DATA_PROTECTION_SET_UP']
          )}
        />
        <AppLayoutTheme>
          <WsTitleBanner title="Data Protection And Privacy Settings" backButton={false} />
        </AppLayoutTheme>
        <Grid container height="70vh" columnGap={6} mx={4} my={2}>
          <Grid xs={2} mt={3} item height="100%" sx={{ backgroundColor: WsColors.White }}>
            <SettingsIndex
              options={[
                {
                  label: 'INDEX',
                  url: '',
                },
                {
                  label: 'Data Subject Access Request Setup',
                  url: '',
                },
              ]}
            />
          </Grid>
          <Grid
            container
            xs={9}
            p={2}
            item
            height="100%"
            px={2}
            mt={3}
            sx={{ backgroundColor: WsColors.White }}
            gap={2}
          >
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <WsTypography variant={WsTypoVariants.TITLE}>Company Name*</WsTypography>
              </Grid>
              <ControlledTextInput
                sx={{ width: '45%', mt: 2 }}
                name={'name'}
                placeholder="Recruitment Team"
                variant="outlined"
                required
                autoComplete="workspaceName"
              />
            </Grid>
            <Grid item container>
              <Grid item xs={3}>
                <WsTypography variant={WsTypoVariants.TITLE}>Company/Website Logo*</WsTypography>
              </Grid>
              <Grid item xs={9}>
                <WsTypography variant={WsTypoVariants.BODY1}>
                  (Add multiple company names with logos urls to setup same GDPR page for multiple
                  website.)
                </WsTypography>
              </Grid>
              <Grid item mt={2} xs={12}>
                <ControlledTextInput
                  sx={{ width: '45%', mr: 1 }}
                  name={'logoName'}
                  placeholder="Company Name/Website Name"
                  variant="outlined"
                  required
                  autoComplete="workspaceName"
                />
                <ControlledTextInput
                  sx={{ width: '45%', mr: 1 }}
                  name={'logoUrl'}
                  placeholder="Company/website Logo URL"
                  variant="outlined"
                  required
                  autoComplete="workspaceName"
                />
                <WsButton variant="contained" onClick={() => onAdd()}>
                  Add
                </WsButton>
              </Grid>
            </Grid>
            <Grid container item>
              {listCompanyInfo.map((val, index) => (
                <>
                  <Grid xs={4} item>
                    <WsTypography variant={WsTypoVariants.TITLE}>{val.companyName}</WsTypography>
                  </Grid>
                  <Grid xs={4} item>
                    <WsTypography variant={WsTypoVariants.BODY1}>{val.companyLink}</WsTypography>
                  </Grid>
                  <Grid xs={4} item>
                    <IconButton onClick={() => onDelete(index)}>
                      <ClearIcon />
                    </IconButton>
                  </Grid>
                </>
              ))}
            </Grid>
            <Grid item container>
              <Grid item xs={3}>
                <WsTypography variant={WsTypoVariants.TITLE}>Data Privacy Email*</WsTypography>
              </Grid>
              <Grid item xs={9}>
                <WsTypography variant={WsTypoVariants.BODY1}>
                  (Candidate data Privacy issue queries will sent to this email. If not provided
                  then workspace owner&apos;s email will be used.)
                </WsTypography>
              </Grid>
              <ControlledTextInput
                sx={{ width: '45%' }}
                name={'email'}
                placeholder="Company Name/Website Name"
                variant="outlined"
                required
                autoComplete="workspaceName"
              />
            </Grid>
            <Grid item>
              <WsTypography variant={WsTypoVariants.BODY1}>
                Below are the list of workspace(s) for the current user (Candidates information will
                be searched against to these workspace(s) teams.)
              </WsTypography>
              <Box mt={2}>
                <WsTagsList
                  chipArray={[userDetails?.length ? userDetails[0]?.name ?? '' : '']}
                  count={3}
                  type={ChipsType.SECONDARY}
                />
              </Box>
              <Box mt={2}>
                <WsButton variant="contained" type="submit">
                  Submit
                </WsButton>
              </Box>
            </Grid>
            <Grid item>
              <WsTypography variant={WsTypoVariants.BODY1}>
                Copy the below URL(s) to setup GDPR link in your company website(s)
              </WsTypography>
              {listCompanyInfo.map((val, index) => (
                <Link
                  key={index}
                  underline={'hover'}
                  color={WsColors.GenericRichBlack}
                  onClick={() =>
                    openUrlInNewTab(dataProtectionLinkGenerate(val.companyName, val.companyId))
                  }
                >
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={WsTypoColors.PRIMARY600}
                    key={index}
                  >
                    {dataProtectionLinkGenerate(val.companyName, val.companyId)}
                  </WsTypography>
                </Link>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};
export default DataProtectionSetUp;
